<template>
  <div>
    <v-autocomplete
      :items="items"
      v-model="selectedPlace"
      :label="label"
      item-text="name"
      item-value="id"
      @change="onChange"
      :outlined="outlined"
      :hide-details="hideDetails"
      :dense="dense"
      :search-input.sync="search"
      :loading="$apollo.loading"
    ></v-autocomplete>
    <div v-if="debug">
      <code>{{ items }}</code>
      <code>{{ selectedPlace }}</code>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { removeGQLTypename, uniqueByProp } from "../../../helpers";

// Minimum characters before switching from the region-scoped default list to a
// global name search. See ticket #11271.
const MIN_SEARCH_CHARS = 2;

const THOMPSONS_PLACES_QUERY = gql`
  query thompsonsPlaces($filter: ThirdPartyMappingFilterType) {
    thompsonsPlaces: thirdPartyMappings(filter: $filter) {
      edges {
        node {
          id
          remoteUid
          matchDetails
        }
      }
    }
  }
`;

const toItem = (edge) => {
  const node = removeGQLTypename(edge);
  return { id: node.remoteUid, name: JSON.parse(node.matchDetails).source };
};

export default {
  name: "thompsons-place-list-select",
  props: {
    label: {
      type: String,
      default: () => "Place",
    },
    outlined: {
      type: Boolean,
      default: () => false,
    },
    hideDetails: {
      type: Boolean,
      default: () => false,
    },
    dense: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: String,
      default: () => "",
    },
    destinationId: {
      type: String,
      default: () => "",
    },
  },
  data() {
    return {
      debug: false,
      selectedPlace: this.value ? this.value : "",
      search: null,
      searchTimeout: null,
      searchDebounceTime: 300,
      // When set (>= MIN_SEARCH_CHARS), the apollo query switches to a global
      // name search; otherwise it falls back to the region-scoped default list.
      nameFilter: null,
      // Label for the preset value (editing a saved search). Resolved via a
      // one-off remote_uid_eq lookup so the box shows the name, not the code,
      // even when the place is outside the current destination's region.
      presetItem: null,
    };
  },
  computed: {
    items() {
      let list = [];
      if (this.thompsonsPlaces && this.thompsonsPlaces.edges) {
        list = uniqueByProp(this.thompsonsPlaces.edges.map(toItem), "name");
      }
      // Keep the selected/preset place in the list so its label is displayed
      // regardless of which query last populated the list.
      if (this.presetItem && !list.some((i) => i.id === this.presetItem.id)) {
        list = [this.presetItem, ...list];
      }
      return list;
    },
  },
  watch: {
    value(newVal) {
      this.selectedPlace = newVal ? newVal : "";
      if (newVal && (!this.presetItem || this.presetItem.id !== newVal)) {
        this.resolvePresetLabel(newVal);
      }
    },
    search(newVal) {
      clearTimeout(this.searchTimeout);
      // Box is showing the current selection (its label, or its raw code before
      // the preset label resolves) — not a user search.
      if (newVal === this.selectedPlace) return;
      const selected = this.items.find((i) => i.id === this.selectedPlace);
      if (selected && selected.name === newVal) return;
      // Empty or too short: show the region-scoped default list.
      if (!newVal || newVal.length < MIN_SEARCH_CHARS) {
        this.nameFilter = null;
        return;
      }
      this.searchTimeout = setTimeout(() => {
        this.nameFilter = newVal;
      }, this.searchDebounceTime);
    },
  },
  created() {
    if (this.value) this.resolvePresetLabel(this.value);
  },
  methods: {
    onChange() {
      // Pin the chosen item so it survives the list reverting to the default.
      const chosen = this.items.find((i) => i.id === this.selectedPlace);
      if (chosen) this.presetItem = chosen;
      this.$emit("input", this.selectedPlace);
    },
    async resolvePresetLabel(code) {
      if (!code) return;
      try {
        const { data } = await this.$apollo.query({
          query: THOMPSONS_PLACES_QUERY,
          variables: {
            filter: {
              vendor_eq: "ThompsonsAfrica",
              mapping_type_eq: "Place",
              remote_uid_eq: code,
            },
          },
          fetchPolicy: "cache-first",
        });
        const edge =
          data && data.thompsonsPlaces && data.thompsonsPlaces.edges[0];
        if (edge) this.presetItem = toItem(edge);
      } catch (e) {
        // Non-fatal: the box falls back to showing the raw code.
        if (this.debug) console.error("Thompsons preset label lookup failed", e);
      }
    },
  },
  apollo: {
    thompsonsPlaces: {
      query: THOMPSONS_PLACES_QUERY,
      variables() {
        const filter = { vendor_eq: "ThompsonsAfrica" };
        if (this.nameFilter && this.nameFilter.length >= MIN_SEARCH_CHARS) {
          // Global, non-region-scoped search (ticket #11271).
          filter.name_cont = this.nameFilter;
        } else {
          // Region-scoped default list (unchanged behaviour).
          filter.destination_id_eq = this.destinationId;
        }
        return { filter };
      },
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/gt/shared/ThompsonsPlaceListSelect.vue